import * as React from "react";
import * as Scrivito from "scrivito";
import { Form, Input, Label } from "reactstrap";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import cx from "classnames";

import { Button, Breadcrumb, Spinner, ObjValidator } from "components";
import { useInfiniteScroll, useTranslate } from "hooks";
import { getCategoryClient } from "utils";

import { SearchResult } from "./SearchResult";
import { getSearchObjects } from "./searchPageHelper";

const ITEMS_PER_PAGE = 10;

const SearchPage = Scrivito.connect(({ page }) => {
  const translate = useTranslate();
  const searchObjects = useMemo(() => getSearchObjects(translate), [translate]);
  const { filter = "all", q = "" } = Scrivito.currentPageParams();

  const [results, setResults] = useState([]);
  const offsetRef = useRef(0);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadItems = useCallback(() => {
    Scrivito.load(() => {
      setIsFetching(true);
      const categoryClient = getCategoryClient() || "DE";
      return Scrivito.Obj.where(
        "_objClass",
        "equals",
        searchObjects[filter].values,
      )
        .andFullTextOf("categoryOwner", "contains", categoryClient)
        .andFullTextOf("*", "containsPrefix", q || "*")
        .andNot("_objClass", "equals", ["Image", "SearchPage", "VideoVimeo"])
        .offset(offsetRef.current)
        .take(ITEMS_PER_PAGE);
    }).then((res) => {
      setIsFetching(false);
      if (res.length !== 0) {
        offsetRef.current += ITEMS_PER_PAGE;
        setResults((prevRes) => [...prevRes, ...res]);
        if (res.length !== ITEMS_PER_PAGE) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    });
  }, [filter, q, searchObjects]);

  const [lastElementRef] = useInfiniteScroll(
    hasMore ? loadItems : () => {},
    isFetching,
  );

  useEffect(() => {
    loadItems();
    setHasMore(true);
    offsetRef.current = 0;
    setResults([]);
  }, [loadItems]);

  const handleSearchStringChange = useCallback(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const formProps = Object.fromEntries(formData);

      Scrivito.navigateTo(Scrivito.currentPage(), {
        q: formProps.searchField,
        filter,
      });
    },
    [filter],
  );

  const handleObjChange = useCallback(
    (e) => {
      const value = e.currentTarget?.dataset?.value;
      if (value && value !== filter) {
        Scrivito.navigateTo(Scrivito.currentPage(), {
          q,
          filter: e.currentTarget.dataset.value,
        });
      }
    },
    [q, filter],
  );
  return (
    <div className="search-page">
      <Scrivito.BackgroundImageTag
        className="background-image"
        style={{ background: { image: page.get("backgroundImage") } }}
      >
        <Breadcrumb page={page} />
        <div className="container d-flex flex-column">
          <div className="mt-2 mb-5 bg-white d-flex align-self-start justify-content-center px-4 py-4 col-4 flex-column">
            <Form onSubmit={handleSearchStringChange}>
              <Label for="searchField">{translate("SEARCH_LABEL")}</Label>
              <Input
                type="text"
                name="searchField"
                defaultValue={q}
                placeholder={translate("SEARCH_PAGE_INPUT_PLACEHOLDER")}
              />
              <Button
                data-testid="SearchPageComponent_search-btn"
                type="submit"
                classNames="mt-3"
              >
                {translate("SEARCH_BUTTON")}
              </Button>
            </Form>
          </div>
        </div>
      </Scrivito.BackgroundImageTag>
      <div className="container d-flex flex-column">
        <div className="d-flex mt-5 align-items-start">
          <div className="w-75 d-flex flex-column mb-5">
            <h1>{translate("SEARCH_PAGE_RESULTS")}</h1>
            {results.length !== 0 ? (
              results.map((item, key) => (
                <SearchResult item={item} key={key} searchedString={q} />
              ))
            ) : (
              <h4>{translate("SEARCH_PAGE_NO_RESULTS")}</h4>
            )}
            {isFetching && <Spinner />}
            <div ref={lastElementRef} />
          </div>
          <div className="w-25 border">
            <h4 className="bg-blue-300 p-3 text-white ">
              {translate("SEARCH_PAGE_CATEGORIES_HEADER")}
            </h4>
            <ul className="my-3">
              <h5 className="text-capitalize">
                {translate("SEARCH_PAGE_CATEGORIES_LIST_TITLE")}
              </h5>
              {Object.keys(searchObjects).map((objKey, key) => (
                <li
                  key={key}
                  className={cx("link-secondary py-1 ms-3", {
                    "text-primary cursor-not-allowed": filter === objKey,
                    "cursor-pointer": filter !== objKey,
                  })}
                  onClick={handleObjChange}
                  data-value={objKey}
                >
                  {searchObjects[objKey].title}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
});

const CheckedPage = (props) => (
  <ObjValidator {...props}>
    <SearchPage {...props} />
  </ObjValidator>
);
export default CheckedPage;
Scrivito.provideComponent("SearchPage", SearchPage);
